"use client"

import Header from "@/components/header"
import Footer from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2, ChevronDown, Check } from "lucide-react"
import { Icons8Icon } from "@/components/ui/icons8-icon"
import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { useChainId, useAccount, useSwitchChain, useWalletClient, usePublicClient, useReadContract } from 'wagmi'
import { parseUnits, formatEther } from 'viem'
import { isTestnet, TESTNET_CHAIN_IDS, MAINNET_CHAIN_IDS } from "@/lib/contracts-config"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { useDeployUniqueToken, DEPLOYER_PROXY_ABI, getDeployerProxyAddress } from "@/lib/deploy-unique-token"

export function CreateTokenContent() {
  const router = useRouter()
  const chainId = useChainId()
  const { isConnected, address } = useAccount()


  // Basic Info
  const [tokenName, setTokenName] = useState("")
  const [tokenSymbol, setTokenSymbol] = useState("")
  const [totalSupply, setTotalSupply] = useState("")
  const [decimals, setDecimals] = useState("18")

  // Features
  const [isMintable, setIsMintable] = useState(false)
  const [isBurnable, setIsBurnable] = useState(false)
  const [isPausable, setIsPausable] = useState(false)

  // Tax Settings (basis points: 100 = 1%)
  const [buyTax, setBuyTax] = useState("0")
  const [sellTax, setSellTax] = useState("0")
  const [transferTax, setTransferTax] = useState("0")

  // Wallets & Router
  const [marketingWallet, setMarketingWallet] = useState("")
  const [routerAddress, setRouterAddress] = useState("")

  // Anti-Whale
  const [antiWhaleEnabled, setAntiWhaleEnabled] = useState(false)
  const [maxTransactionPercent, setMaxTransactionPercent] = useState("1") // % of supply
  const [maxWalletPercent, setMaxWalletPercent] = useState("2") // % of supply

  // Reflection
  const [reflectionEnabled, setReflectionEnabled] = useState(false)
  const [reflectionPercent, setReflectionPercent] = useState("0")

  // Other
  const [autoRenounce, setAutoRenounce] = useState(false)
  const [tokenNotes, setTokenNotes] = useState("")

  // Social Links
  const [website, setWebsite] = useState("")
  const [twitterHandle, setTwitterHandle] = useState("")
  const [telegramLink, setTelegramLink] = useState("")

  // NEW: Security Features (V2)
  const [blacklistEnabled, setBlacklistEnabled] = useState(false)
  const [tradeCooldownEnabled, setTradeCooldownEnabled] = useState(false)
  const [tradeCooldownTime, setTradeCooldownTime] = useState("30") // seconds

  // NEW: Early Sell Penalty
  const [earlySellPenaltyEnabled, setEarlySellPenaltyEnabled] = useState(false)
  const [earlySellPenaltyDuration, setEarlySellPenaltyDuration] = useState("3600") // 1 hour in seconds
  const [earlySellPenaltyTax, setEarlySellPenaltyTax] = useState("5") // 5% extra tax

  // NEW: Vesting/Lock
  const [vestingEnabled, setVestingEnabled] = useState(false)
  const [vestingAmount, setVestingAmount] = useState("") // Amount to lock
  const [vestingDays, setVestingDays] = useState("30") // Days until unlock

  // NEW: EIP-2612 Permit (Gasless Approvals)
  const [permitEnabled, setPermitEnabled] = useState(false)

  // NEW: Staking Support
  const [stakingEnabled, setStakingEnabled] = useState(false)

  const [deployError, setDeployError] = useState<string | null>(null)

  // Network switching hook
  const { switchChain, isPending: isSwitchingNetwork } = useSwitchChain()

  // Available networks based on testnet/mainnet mode
  const networks = isTestnet
    ? [
      { id: "BSC", name: "BSC Testnet", fullName: "BSC Testnet", chainId: TESTNET_CHAIN_IDS.BSC_TESTNET },
      { id: "ETH", name: "Sepolia", fullName: "Sepolia Testnet", chainId: TESTNET_CHAIN_IDS.SEPOLIA },
      { id: "POLYGON", name: "Amoy", fullName: "Polygon Amoy", chainId: TESTNET_CHAIN_IDS.POLYGON_AMOY },
      { id: "BASE", name: "Base Sepolia", fullName: "Base Sepolia", chainId: TESTNET_CHAIN_IDS.BASE_SEPOLIA },
    ]
    : [
      { id: "BSC", name: "BSC", fullName: "BNB Smart Chain", chainId: MAINNET_CHAIN_IDS.BSC },
      { id: "ETH", name: "Ethereum", fullName: "Ethereum", chainId: MAINNET_CHAIN_IDS.ETH },
      { id: "POLYGON", name: "Polygon", fullName: "Polygon", chainId: MAINNET_CHAIN_IDS.POLYGON },
      { id: "BASE", name: "Base", fullName: "Base", chainId: MAINNET_CHAIN_IDS.BASE },
    ]

  // Get current network
  const currentNetwork = networks.find(n => n.chainId === chainId)

  // Handle network switch
  const handleNetworkSwitch = async (network: typeof networks[0]) => {
    if (network.chainId === chainId) return
    try {
      await switchChain({ chainId: network.chainId })
    } catch (error) {
      console.error('Failed to switch network:', error)
    }
  }

  // Wagmi clients for direct deployment
  const { data: walletClient } = useWalletClient()
  const publicClient = usePublicClient()

  // Use the unique token deployment hook (direct bytecode deployment)
  const {
    compileToken,
    deployToken,
    saveTokenToDatabase,
    isCompiling,
    isDeploying,
    compileError,
    deployError: hookDeployError,
    compiledData,
    deployedAddress,
    txHash: deployTxHash,
    isPending
  } = useDeployUniqueToken()

  // Track deployment stages
  const [deploymentStage, setDeploymentStage] = useState<'idle' | 'compiling' | 'deploying' | 'saving' | 'done'>('idle')

  // Get network name from chain ID
  const getNetworkName = () => {
    if (isTestnet) {
      if (chainId === TESTNET_CHAIN_IDS.BSC_TESTNET) return 'bsc-testnet'
      if (chainId === TESTNET_CHAIN_IDS.SEPOLIA) return 'sepolia'
      if (chainId === TESTNET_CHAIN_IDS.POLYGON_AMOY) return 'polygon-amoy'
      if (chainId === TESTNET_CHAIN_IDS.BASE_SEPOLIA) return 'base-sepolia'
    } else {
      if (chainId === MAINNET_CHAIN_IDS.BSC) return 'bsc'
      if (chainId === MAINNET_CHAIN_IDS.ETH) return 'ethereum'
      if (chainId === MAINNET_CHAIN_IDS.POLYGON) return 'polygon'
      if (chainId === MAINNET_CHAIN_IDS.BASE) return 'base'
    }
    return 'unknown'
  }

  // Get native currency for current chain
  const getNativeCurrency = (): string => {
    if (isTestnet) {
      if (chainId === TESTNET_CHAIN_IDS.BSC_TESTNET) return 'tBNB'
      if (chainId === TESTNET_CHAIN_IDS.SEPOLIA) return 'ETH'
      if (chainId === TESTNET_CHAIN_IDS.POLYGON_AMOY) return 'POL'
      if (chainId === TESTNET_CHAIN_IDS.BASE_SEPOLIA) return 'ETH'
    } else {
      if (chainId === MAINNET_CHAIN_IDS.BSC) return 'BNB'
      if (chainId === MAINNET_CHAIN_IDS.ETH) return 'ETH'
      if (chainId === MAINNET_CHAIN_IDS.POLYGON) return 'POL'
      if (chainId === MAINNET_CHAIN_IDS.BASE) return 'ETH'
    }
    return 'Native'
  }

  const nativeCurrency = getNativeCurrency()

  // Handle deploy - NEW: Direct bytecode deployment with unique source code
  const handleDeploy = async () => {
    // Prevent double submission
    if (deploymentStage !== 'idle') {
      console.log('Deployment already in progress, ignoring duplicate call')
      return
    }

    setDeployError(null)

    if (!isConnected || !address) {
      setDeployError("Please connect your wallet first")
      return
    }

    if (!walletClient) {
      setDeployError("Wallet client not available. Please reconnect your wallet.")
      return
    }

    if (!publicClient) {
      setDeployError("Public client not available.")
      return
    }

    if (!tokenName || !tokenSymbol || !totalSupply) {
      setDeployError("Please fill in all required fields")
      return
    }

    try {
      // Stage 1: Compile unique source code
      setDeploymentStage('compiling')

      const compileResult = await compileToken({
        name: tokenName,
        symbol: tokenSymbol,
        decimals: parseInt(decimals),
        totalSupply: totalSupply, // Raw supply, decimals applied in template
        ownerAddress: address,
        chainId: chainId || 97,
        // Features
        reflectionEnabled: reflectionEnabled,
        autoLiquidityEnabled: false, // Disabled for now
        isMintable: isMintable,
        isPausable: isPausable,
        // Social links
        website: website || undefined,
        twitter: twitterHandle || undefined,
        telegram: telegramLink || undefined,
        // NEW: Security features (V2)
        blacklistEnabled: blacklistEnabled,
        tradeCooldownEnabled: tradeCooldownEnabled,
        tradeCooldownTime: parseInt(tradeCooldownTime) || 0,
        // NEW: Early Sell Penalty
        earlySellPenaltyEnabled: earlySellPenaltyEnabled,
        earlySellPenaltyDuration: parseInt(earlySellPenaltyDuration) || 0,
        earlySellPenaltyTax: Math.floor(parseFloat(earlySellPenaltyTax) * 100), // Convert % to basis points
        // NEW: Vesting
        vestingEnabled: vestingEnabled,
        vestingAmount: vestingAmount || '0',
        vestingReleaseTime: vestingEnabled ? Math.floor(Date.now() / 1000) + (parseInt(vestingDays) * 86400) : 0, // Unix timestamp
        vestingBeneficiary: address, // Beneficiary is the deployer
        // NEW: Permit (EIP-2612)
        permitEnabled: permitEnabled,
        // NEW: Staking Support
        stakingEnabled: stakingEnabled,
        // Tax (basis points)
        buyTax: Math.floor(parseFloat(buyTax) * 100),
        sellTax: Math.floor(parseFloat(sellTax) * 100),
        transferTax: Math.floor(parseFloat(transferTax) * 100),
        reflectionFee: Math.floor(parseFloat(reflectionPercent) * 100),
        liquidityFee: 0,
        // Anti-whale
        antiWhaleEnabled: antiWhaleEnabled,
        maxTxPercent: parseFloat(maxTransactionPercent),
        maxWalletPercent: parseFloat(maxWalletPercent),
        // Wallets
        marketingWallet: marketingWallet || address,
      })

      if (!compileResult.success || !compileResult.bytecode) {
        const errorDetails = compileResult.details?.join(', ') || ''
        const fullError = errorDetails
          ? `${compileResult.error || "Compilation failed"}: ${errorDetails}`
          : (compileResult.error || compileResult.message || "Compilation failed")
        setDeployError(fullError)
        setDeploymentStage('idle')
        return
      }

      // Stage 2: Deploy the compiled bytecode
      setDeploymentStage('deploying')

      // NEW: Calculate TOTAL fee (Base + Advanced) for the proxy
      const totalFee = undefined // Let the hook read it from the contract
      console.log(`[CreateToken] Fee will be read from contract dynamically`)

      // PASS compileResult DIRECTLY to avoid state race condition
      const deployment = await deployToken(walletClient, publicClient, address, totalFee, compileResult)

      if (!deployment) {
        setDeployError(hookDeployError || "Deployment failed")
        setDeploymentStage('idle')
        return
      }

      // Stage 3: Save to database
      setDeploymentStage('saving')

      // PASS compileResult DIRECTLY here too
      await saveTokenToDatabase(
        deployment.address,
        deployment.hash,
        {
          name: tokenName,
          symbol: tokenSymbol,
          decimals: parseInt(decimals),
          totalSupply: totalSupply,
          ownerAddress: address,
          chainId: chainId || 97,
          reflectionEnabled: reflectionEnabled,
          autoLiquidityEnabled: false,
          isMintable: isMintable,
          isPausable: isPausable,
          buyTax: Math.floor(parseFloat(buyTax) * 100),
          sellTax: Math.floor(parseFloat(sellTax) * 100),
          transferTax: Math.floor(parseFloat(transferTax) * 100),
          reflectionFee: Math.floor(parseFloat(reflectionPercent) * 100),
          liquidityFee: 0,
          antiWhaleEnabled: antiWhaleEnabled,
          maxTxPercent: parseFloat(maxTransactionPercent),
          maxWalletPercent: parseFloat(maxWalletPercent),
          marketingWallet: marketingWallet || address,
        },
        address, // creatorWallet
        compileResult
      )

      // Stage 4: Redirect to success page
      setDeploymentStage('done')

      const params = new URLSearchParams({
        name: tokenName,
        symbol: tokenSymbol,
        supply: totalSupply,
        decimals: decimals,
        mintable: isMintable.toString(),
        burnable: isBurnable.toString(),
        pausable: isPausable.toString(),
        network: getNetworkName(),
        tx: deployment.hash,
        address: deployment.address,
      })
      router.push(`/create-token/success?${params.toString()}`)

    } catch (err: any) {
      setDeployError(err.message || "Failed to create token")
      setDeploymentStage('idle')
    }
  }

  // Get deployment status message
  const getDeploymentStatus = () => {
    switch (deploymentStage) {
      case 'compiling': return 'Compiling unique source code...'
      case 'deploying': return 'Deploying contract... (confirm in wallet)'
      case 'saving': return 'Saving to database...'
      case 'done': return 'Success! Redirecting...'
      default: return null
    }
  }

  // Check if any advanced features are enabled (matches hook's isAdvanced logic)
  const hasAdvancedFeatures = parseFloat(buyTax) > 0 || parseFloat(sellTax) > 0 || parseFloat(transferTax) > 0 ||
    antiWhaleEnabled || reflectionEnabled || isMintable || isPausable || stakingEnabled

  // Get the TokenDeployerProxy address for the current chain
  const deployerProxyAddress = getDeployerProxyAddress(chainId)

  // Read fee dynamically from the TokenDeployerProxy contract (USD → native via Chainlink)
  const { data: onChainFeeData } = useReadContract({
    address: deployerProxyAddress as `0x${string}` | undefined,
    abi: DEPLOYER_PROXY_ABI,
    functionName: 'getCreationFeeInNative',
    query: { enabled: !!deployerProxyAddress },
  })

  // Parse the on-chain fee data: [fee (native wei), feeUsd (8 decimals)]
  const onChainFeeWei = onChainFeeData ? (onChainFeeData as [bigint, bigint])[0] : null
  const onChainFeeUsd = onChainFeeData ? Number((onChainFeeData as [bigint, bigint])[1]) / 1e8 : null

  // Display the creation fee from contract (with fallback)
  const baseFeeDisplay = onChainFeeWei ? parseFloat(formatEther(onChainFeeWei)).toFixed(4) : '...'
  const usdDisplay = onChainFeeUsd ? `~$${onChainFeeUsd.toFixed(0)} USD` : ''
  // No separate advanced fee — the contract handles it as one flat fee
  const totalFeeDisplay = baseFeeDisplay

  return (
    <div className="min-h-screen flex flex-col pt-20">
      <Header />

      <main className="flex-1 py-8 md:py-12 lg:py-16">
        <div className="container max-w-7xl px-4 md:px-6">
          {/* Header */}
          <div className="space-y-4 mb-8 md:mb-12 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">Create Your Token</h1>
            <p className="text-base md:text-lg text-muted-foreground text-balance max-w-2xl mx-auto">
              Deploy your own cryptocurrency token in minutes with no coding required
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12">
            <Card className="p-4 bg-card/50">
              <div className="flex flex-col items-center text-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Icons8Icon name="lightning-bolt" size="lg" />
                </div>
                <h3 className="font-semibold text-sm">Instant Deploy</h3>
                <p className="text-xs text-muted-foreground">Launch in under 5 minutes</p>
              </div>
            </Card>

            <Card className="p-4 bg-card/50">
              <div className="flex flex-col items-center text-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Icons8Icon name="shield" size="lg" />
                </div>
                <h3 className="font-semibold text-sm">Audited Code</h3>
                <p className="text-xs text-muted-foreground">Battle-tested contracts</p>
              </div>
            </Card>

            <Card className="p-4 bg-card/50">
              <div className="flex flex-col items-center text-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Icons8Icon name="trending-up" size="lg" />
                </div>
                <h3 className="font-semibold text-sm">Advanced Features</h3>
                <p className="text-xs text-muted-foreground">Tax, burn, and more</p>
              </div>
            </Card>

            <Card className="p-4 bg-card/50">
              <div className="flex flex-col items-center text-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Icons8Icon name="lock" size="lg" />
                </div>
                <h3 className="font-semibold text-sm">Renounce Option</h3>
                <p className="text-xs text-muted-foreground">Full ownership control</p>
              </div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-[1fr,380px] gap-6 lg:gap-8 items-start">
            {/* Main Form */}
            <div className="space-y-6 w-full">
              <Card className="p-4 md:p-6">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Token Configuration</h2>

                <Tabs defaultValue="basic" className="space-y-6">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="advanced">Advanced Features</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4 md:space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="network">Network *</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full justify-between bg-transparent" disabled={isSwitchingNetwork}>
                            <span>{isSwitchingNetwork ? "Switching..." : (currentNetwork?.fullName || "Select Network")}</span>
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                          {networks.map((network) => (
                            <DropdownMenuItem
                              key={network.id}
                              onClick={() => handleNetworkSwitch(network)}
                              className={chainId === network.chainId ? "bg-primary/10" : ""}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{network.fullName}</span>
                                <span className="text-xs text-muted-foreground">
                                  {chainId === network.chainId ? "✓ Connected" : network.name}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {!isConnected && (
                        <p className="text-xs text-muted-foreground">Connect wallet to switch networks</p>
                      )}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="token-name">Token Name *</Label>
                        <Input
                          id="token-name"
                          placeholder="e.g., My Token"
                          value={tokenName}
                          onChange={(e) => setTokenName(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="token-symbol">Token Symbol *</Label>
                        <Input
                          id="token-symbol"
                          placeholder="e.g., MTK"
                          className="uppercase"
                          value={tokenSymbol}
                          onChange={(e) => setTokenSymbol(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="total-supply">Total Supply *</Label>
                      <Input
                        id="total-supply"
                        type="number"
                        placeholder="1000000000"
                        value={totalSupply}
                        onChange={(e) => setTotalSupply(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">Total number of tokens that will ever exist</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="decimals">Decimals *</Label>
                      <Select value={decimals} onValueChange={setDecimals}>
                        <SelectTrigger id="decimals">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="9">9 Decimals</SelectItem>
                          <SelectItem value="18">18 Decimals (Standard)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Social Links */}
                    <div className="space-y-4 pt-4 border-t border-border/40">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Social Links (Optional)</h3>
                      <p className="text-xs text-muted-foreground">These will be embedded in your token contract source code</p>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="website">Website</Label>
                          <Input
                            id="website"
                            placeholder="https://mytoken.com"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="twitter">Twitter</Label>
                          <Input
                            id="twitter"
                            placeholder="@mytoken"
                            value={twitterHandle}
                            onChange={(e) => setTwitterHandle(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="telegram">Telegram</Label>
                          <Input
                            id="telegram"
                            placeholder="https://t.me/mytoken"
                            value={telegramLink}
                            onChange={(e) => setTelegramLink(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="advanced" className="space-y-6">
                    {/* Fee Notice */}
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Note:</span> The creation fee includes all advanced features. Current fee: <span className="font-semibold text-primary">{totalFeeDisplay} {nativeCurrency}</span>{usdDisplay && <> ({usdDisplay})</>}.
                      </p>
                    </div>

                    {/* Token Features Section */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Token Features</h3>

                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="mintable"
                          checked={isMintable}
                          onCheckedChange={(checked) => setIsMintable(checked as boolean)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="mintable" className="font-medium cursor-pointer">
                            Mintable
                          </Label>
                          <p className="text-xs text-muted-foreground">Allow creating new tokens after deployment</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="burnable"
                          checked={isBurnable}
                          onCheckedChange={(checked) => setIsBurnable(checked as boolean)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="burnable" className="font-medium cursor-pointer">
                            Burnable
                          </Label>
                          <p className="text-xs text-muted-foreground">Allow permanent destruction of tokens (always enabled)</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="pausable"
                          checked={isPausable}
                          onCheckedChange={(checked) => setIsPausable(checked as boolean)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="pausable" className="font-medium cursor-pointer">
                            Pausable
                          </Label>
                          <p className="text-xs text-muted-foreground">Enable pausing all token transfers</p>
                        </div>
                      </div>

                      {/* NEW: Blacklist Toggle */}
                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="blacklist"
                          checked={blacklistEnabled}
                          onCheckedChange={(checked) => setBlacklistEnabled(checked as boolean)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="blacklist" className="font-medium cursor-pointer">
                            Blacklist 🚫
                          </Label>
                          <p className="text-xs text-muted-foreground">Block specific addresses from transferring</p>
                        </div>
                      </div>

                      {/* NEW: Trade Cooldown Toggle */}
                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="cooldown"
                          checked={tradeCooldownEnabled}
                          onCheckedChange={(checked) => setTradeCooldownEnabled(checked as boolean)}
                        />
                        <div className="space-y-1 flex-1">
                          <Label htmlFor="cooldown" className="font-medium cursor-pointer">
                            Trade Cooldown ⏱️
                          </Label>
                          <p className="text-xs text-muted-foreground">Anti-bot: require time between trades</p>
                          {tradeCooldownEnabled && (
                            <div className="mt-2">
                              <Label htmlFor="cooldown-time" className="text-xs">Cooldown (seconds)</Label>
                              <Input
                                id="cooldown-time"
                                type="number"
                                placeholder="30"
                                value={tradeCooldownTime}
                                onChange={(e) => setTradeCooldownTime(e.target.value)}
                                min="0" max="600"
                                className="mt-1 w-24"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* NEW: Early Sell Penalty Toggle */}
                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="earlySellPenalty"
                          checked={earlySellPenaltyEnabled}
                          onCheckedChange={(checked) => setEarlySellPenaltyEnabled(checked as boolean)}
                        />
                        <div className="space-y-1 flex-1">
                          <Label htmlFor="earlySellPenalty" className="font-medium cursor-pointer">
                            Early Sell Penalty 💸
                          </Label>
                          <p className="text-xs text-muted-foreground">Extra tax on quick flips (anti-dump)</p>
                          {earlySellPenaltyEnabled && (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <div>
                                <Label htmlFor="penaltyDuration" className="text-xs">Duration (hours)</Label>
                                <Input
                                  id="penaltyDuration"
                                  type="number"
                                  placeholder="1"
                                  value={Math.floor(parseInt(earlySellPenaltyDuration) / 3600) || 1}
                                  onChange={(e) => setEarlySellPenaltyDuration((parseInt(e.target.value) * 3600).toString())}
                                  min="1" max="168"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label htmlFor="penaltyTax" className="text-xs">Extra Tax (%)</Label>
                                <Input
                                  id="penaltyTax"
                                  type="number"
                                  placeholder="5"
                                  value={earlySellPenaltyTax}
                                  onChange={(e) => setEarlySellPenaltyTax(e.target.value)}
                                  min="1" max="25"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>


                      {/* NEW: Permit (Gasless Approvals) Toggle */}
                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="permit"
                          checked={permitEnabled}
                          onCheckedChange={(checked) => setPermitEnabled(checked as boolean)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="permit" className="font-medium cursor-pointer">
                            Gasless Approvals (EIP-2612) ⛽
                          </Label>
                          <p className="text-xs text-muted-foreground">Allow approve via signatures (no gas)</p>
                        </div>
                      </div>

                      {/* NEW: Staking Support Toggle */}
                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="staking"
                          checked={stakingEnabled}
                          onCheckedChange={(checked) => setStakingEnabled(checked as boolean)}
                        />
                        <div className="space-y-1">
                          <Label htmlFor="staking" className="font-medium cursor-pointer">
                            Staking Support 🥩
                          </Label>
                          <p className="text-xs text-muted-foreground">Enable staking pool integration (stakeFor, approveAndStake)</p>
                        </div>
                      </div>
                    </div>

                    {/* Transaction Tax Section */}

                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Transaction Tax</h3>

                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-primary/20 bg-primary/5">
                        <Checkbox
                          id="tax"
                          checked={parseFloat(buyTax) > 0 || parseFloat(sellTax) > 0 || parseFloat(transferTax) > 0}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              setBuyTax("0")
                              setSellTax("0")
                              setTransferTax("0")
                            }
                          }}
                        />
                        <div className="space-y-3 flex-1">
                          <div>
                            <Label htmlFor="tax" className="font-medium cursor-pointer">
                              Enable Transaction Tax
                            </Label>
                            <p className="text-xs text-muted-foreground">Apply a tax on each transaction</p>
                          </div>
                          <div className="grid sm:grid-cols-3 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="buy-tax" className="text-xs">Buy Tax (%)</Label>
                              <Input
                                id="buy-tax"
                                type="number"
                                placeholder="0"
                                value={buyTax}
                                onChange={(e) => setBuyTax(e.target.value)}
                                min="0" max="25"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="sell-tax" className="text-xs">Sell Tax (%)</Label>
                              <Input
                                id="sell-tax"
                                type="number"
                                placeholder="0"
                                value={sellTax}
                                onChange={(e) => setSellTax(e.target.value)}
                                min="0" max="25"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="transfer-tax" className="text-xs">Transfer Tax (%)</Label>
                              <Input
                                id="transfer-tax"
                                type="number"
                                placeholder="0"
                                value={transferTax}
                                onChange={(e) => setTransferTax(e.target.value)}
                                min="0" max="25"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Reflection/Rewards */}
                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="reflection"
                          checked={reflectionEnabled}
                          onCheckedChange={(checked) => setReflectionEnabled(checked as boolean)}
                        />
                        <div className="space-y-2 flex-1">
                          <Label htmlFor="reflection" className="font-medium cursor-pointer">
                            Reflection/Rewards
                          </Label>
                          <p className="text-xs text-muted-foreground">Automatically distribute rewards to holders</p>
                          {reflectionEnabled && (
                            <div className="pt-2">
                              <Label htmlFor="reflection-percent" className="text-xs">Reflection % of Tax</Label>
                              <Input
                                id="reflection-percent"
                                type="number"
                                placeholder="50"
                                value={reflectionPercent}
                                onChange={(e) => setReflectionPercent(e.target.value)}
                                min="0" max="100"
                                className="mt-1"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Anti-Whale Protection */}
                      <div className="flex items-start space-x-3 p-4 rounded-lg border border-border/40 bg-muted/30">
                        <Checkbox
                          id="anti-whale"
                          checked={antiWhaleEnabled}
                          onCheckedChange={(checked) => setAntiWhaleEnabled(checked as boolean)}
                        />
                        <div className="space-y-2 flex-1">
                          <Label htmlFor="anti-whale" className="font-medium cursor-pointer">
                            Anti-Whale Protection
                          </Label>
                          <p className="text-xs text-muted-foreground">Limit max transaction & wallet amounts</p>
                          {antiWhaleEnabled && (
                            <div className="grid grid-cols-2 gap-3 pt-2">
                              <div className="space-y-1">
                                <Label htmlFor="max-tx-percent" className="text-xs">Max Tx (%)</Label>
                                <Input
                                  id="max-tx-percent"
                                  type="number"
                                  placeholder="1"
                                  value={maxTransactionPercent}
                                  onChange={(e) => setMaxTransactionPercent(e.target.value)}
                                  min="0.1" max="100"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor="max-wallet-percent" className="text-xs">Max Wallet (%)</Label>
                                <Input
                                  id="max-wallet-percent"
                                  type="number"
                                  placeholder="2"
                                  value={maxWalletPercent}
                                  onChange={(e) => setMaxWalletPercent(e.target.value)}
                                  min="0.1" max="100"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* DEX & Wallet Settings */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">DEX & Wallet Settings</h3>

                      <div className="space-y-2">
                        <Label htmlFor="router-address">Router Address (DEX)</Label>
                        <Input
                          id="router-address"
                          placeholder="0x10ED43C718714eb63d5aA57B78B54704E256024E"
                          className="font-mono"
                          value={routerAddress}
                          onChange={(e) => setRouterAddress(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">PancakeSwap Router V2 (BSC): 0x10ED43C718714eb63d5aA57B78B54704E256024E</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="marketing-wallet">Marketing Wallet</Label>
                        <Input
                          id="marketing-wallet"
                          placeholder="0x... (defaults to your wallet)"
                          className="font-mono"
                          value={marketingWallet}
                          onChange={(e) => setMarketingWallet(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Wallet to receive tax fees (leave empty to use your wallet)</p>
                      </div>
                    </div>

                    {/* Ownership Settings */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Ownership Settings</h3>

                      <div className="p-4 rounded-lg border border-destructive/20 bg-destructive/5">
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="renounce"
                            checked={autoRenounce}
                            onCheckedChange={(checked) => setAutoRenounce(checked as boolean)}
                          />
                          <div className="space-y-1">
                            <Label htmlFor="renounce" className="font-medium cursor-pointer text-destructive">
                              Auto-Renounce Ownership
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              ⚠️ Automatically renounce contract ownership after deployment. This is PERMANENT and cannot be undone! You will lose all owner privileges.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="token-notes">Contract Notes</Label>
                        <Textarea
                          id="token-notes"
                          placeholder="Add notes that will be embedded in your token contract (e.g., website, telegram, description)"
                          rows={4}
                          value={tokenNotes}
                          onChange={(e) => setTokenNotes(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">These notes will be visible in the contract source code on the block explorer</p>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>


            </div>

            <div className="lg:sticky lg:top-24 space-y-6 w-full">
              {/* Compact Cost Summary */}
              <Card className="p-3 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold">Cost Summary</span>
                  <div className="text-right">
                    <span className="font-bold text-primary">{totalFeeDisplay} {nativeCurrency}</span>
                    {usdDisplay && <span className="text-xs text-muted-foreground ml-1">({usdDisplay})</span>}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex justify-between">
                    <span>Platform Fee</span>
                    <span>{baseFeeDisplay} {nativeCurrency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Network (est.)</span>
                    <span>~0.001 {nativeCurrency}</span>
                  </div>
                </div>
              </Card>

              {/* What You Get - Compact */}
              <Card className="p-3 bg-card/50">
                <h3 className="font-semibold text-sm mb-2">What You Get</h3>
                <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icons8Icon name="coins" size="xs" />
                    <span>ERC-20/BEP-20 Token</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icons8Icon name="shield" size="xs" />
                    <span>Audited Contract</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icons8Icon name="trending-up" size="xs" />
                    <span>Auto Liquidity</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icons8Icon name="lock" size="xs" />
                    <span>Ownership Control</span>
                  </div>
                </div>
              </Card>

              {/* Error Display */}
              {(deployError || compileError || hookDeployError) && (
                <Card className="p-3 border-destructive bg-destructive/10">
                  <div className="flex items-start gap-2">
                    <Icons8Icon name="eye" size="sm" />
                    <div>
                      <p className="text-sm font-medium text-destructive">Deployment Error</p>
                      <p className="text-xs text-destructive/80">
                        {deployError || compileError || hookDeployError || 'Transaction failed'}
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              <Button
                className="w-full h-12 md:h-14 text-base md:text-lg font-semibold"
                size="lg"
                onClick={handleDeploy}
                disabled={isPending || !isConnected}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {getDeploymentStatus() || "Processing..."}
                    {deploymentStage === 'compiling' ? 'Preparing Source...' :
                      deploymentStage === 'deploying' ? 'Deploying...' :
                        deploymentStage === 'saving' ? 'Saving Details...' :
                          'Deploy Token'}
                  </>
                ) : !isConnected ? (
                  "Connect Wallet First"
                ) : (
                  <>
                    <Icons8Icon name="coins" size="md" className="mr-2" />
                    {`Deploy Token`}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground px-2">
                By deploying, you agree to our Terms of Service and acknowledge the risks. Gas fees apply.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* SEO explainer — below the form, above footer */}
      <section className="container max-w-3xl mx-auto pb-16">
        <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
          <h2 className="text-base font-semibold text-foreground">Create Your Own Crypto Token</h2>
          <p>
            TokenKickstarter lets you deploy a fully functional ERC-20 token without writing any code.
            Choose your token name, symbol, supply, and features — then deploy directly to the blockchain
            with one click.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Buy/sell taxes</strong> — set custom tax percentages that fund your project treasury.</li>
            <li><strong>Anti-whale limits</strong> — cap the maximum tokens a single wallet can hold or trade.</li>
            <li><strong>Auto-liquidity</strong> — a share of each transaction is added to the liquidity pool.</li>
            <li><strong>Ownership controls</strong> — renounce, transfer, or keep ownership of the contract.</li>
          </ul>
          <p>
            Tokens are deployed using audited, OpenZeppelin-based smart contracts on BNB Chain, Ethereum,
            Base, Polygon, and other supported networks. After deployment, you can list your token on a DEX,
            launch a presale, or lock tokens right from your dashboard.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  )
}
